import React, { useEffect, useMemo, useRef, useState } from 'react';
import { useParams, useSearchParams } from 'react-router-dom';
import { api } from '../services/api';

/**
 * «O que quer fazer com a Pagali?» — destino dos botões do email de follow-up que
 * as entidades novas recebem no dia seguinte ao registo. O botão de um serviço traz
 * ?s=CODIGO e fica gravado logo ao abrir; daqui marca-se o resto do catálogo.
 */
interface Servico { codigo: string; nome: string; grupo: string; descricao: string }
interface Dados { nome: string; sugeridos: string[]; catalogo: Servico[]; escolhidos: string[] }

const InteresseEntidade: React.FC = () => {
  const { token } = useParams<{ token: string }>();
  const [params] = useSearchParams();
  const doEmail = (params.get('s') || '').toUpperCase();
  const [dados, setDados] = useState<Dados | null>(null);
  const [erro, setErro] = useState<string | null>(null);
  const [marcados, setMarcados] = useState<Set<string>>(new Set());
  const [enviando, setEnviando] = useState(false);
  const [enviado, setEnviado] = useState(false);
  const gravouClique = useRef(false);

  useEffect(() => {
    (async () => {
      try {
        const r = await api.get(`/public/interesse/${token}`);
        const d: Dados = r.data.data;
        // O clique no email conta como interesse — grava-se já, uma vez.
        if (doEmail && !gravouClique.current && d.catalogo.some((s) => s.codigo === doEmail)) {
          gravouClique.current = true;
          await api.post(`/public/interesse/${token}`, { servicos: [doEmail], origem: 'email' }).catch(() => {});
          if (!d.escolhidos.includes(doEmail)) d.escolhidos = [...d.escolhidos, doEmail];
        }
        setDados(d);
        setMarcados(new Set(d.escolhidos));
      } catch (e: any) {
        setErro(e?.response?.data?.error || 'Não foi possível abrir este link.');
      }
    })();
  }, [token, doEmail]);

  const grupos = useMemo(() => {
    const m = new Map<string, Servico[]>();
    for (const s of dados?.catalogo || []) m.set(s.grupo, [...(m.get(s.grupo) || []), s]);
    return Array.from(m.entries());
  }, [dados]);

  // Cada clique grava logo — não há nada por «enviar» que se possa perder.
  // Um serviço registado já não se desmarca: a equipa comercial já foi avisada.
  const alternar = async (c: string) => {
    if (!dados || marcados.has(c) || enviando) return;
    setEnviando(true);
    setMarcados((prev) => new Set(prev).add(c));
    try {
      await api.post(`/public/interesse/${token}`, { servicos: [c], origem: 'pagina' });
      setDados((d) => (d ? { ...d, escolhidos: Array.from(new Set([...d.escolhidos, c])) } : d));
    } catch (e: any) {
      setMarcados((prev) => { const n = new Set(prev); n.delete(c); return n; });
      setErro(e?.response?.data?.error || 'Não foi possível gravar. Tente de novo.');
    } finally {
      setEnviando(false);
    }
  };

  if (erro) {
    return (
      <div className="min-h-screen flex items-center justify-center bg-gray-50 px-4">
        <div className="bg-white rounded-2xl shadow p-8 max-w-md text-center">
          <p className="text-lg font-semibold text-gray-800">{erro}</p>
          <p className="text-sm text-gray-500 mt-2">Escreva-nos para admin@pagali.cv e ajudamos.</p>
        </div>
      </div>
    );
  }

  if (!dados) {
    return <div className="min-h-screen flex items-center justify-center text-gray-500">A carregar…</div>;
  }

  if (enviado) {
    return (
      <div className="min-h-screen flex items-center justify-center bg-gray-50 px-4">
        <div className="bg-white rounded-2xl shadow p-8 max-w-md text-center">
          <p className="text-2xl font-bold text-pagali-purple">Obrigado, {dados.nome}!</p>
          <p className="text-gray-600 mt-3">A nossa equipa vai entrar em contacto consigo em breve para pôr estes serviços a funcionar.</p>
          <a href="/" className="inline-block mt-6 bg-pagali-purple text-white rounded-xl px-6 py-3 font-semibold">Ir para a Pagali</a>
        </div>
      </div>
    );
  }

  const total = dados.escolhidos.length;

  return (
    <div className="min-h-screen bg-gray-50 px-4 py-8">
      <div className="max-w-3xl mx-auto">
        <div className="bg-pagali-purple rounded-2xl text-white p-6 sm:p-8">
          <p className="text-sm font-bold tracking-widest opacity-80">PAGALI</p>
          <h1 className="text-2xl sm:text-3xl font-bold mt-2 !text-white">O que quer fazer com a Pagali?</h1>
          <p className="mt-2 opacity-90">
            Olá {dados.nome}. Toque nos serviços que lhe interessam — cada um fica registado logo — e a nossa equipa entra em contacto.
          </p>
          {doEmail && dados.escolhidos.includes(doEmail) && (
            <p className="mt-4 bg-white/15 rounded-xl px-4 py-2 text-sm">
              ✓ Registámos o seu interesse. Pode juntar mais serviços abaixo.
            </p>
          )}
        </div>

        {grupos.map(([grupo, servicos]) => (
          <div key={grupo} className="mt-6">
            <h2 className="text-sm font-bold uppercase tracking-wide text-gray-500 mb-2">{grupo}</h2>
            <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
              {servicos.map((s) => {
                const ativo = marcados.has(s.codigo);
                const registado = dados.escolhidos.includes(s.codigo);
                const sugerido = dados.sugeridos.includes(s.codigo);
                return (
                  <button
                    key={s.codigo}
                    type="button"
                    onClick={() => alternar(s.codigo)}
                    aria-pressed={ativo}
                    className={`text-left rounded-xl border p-4 transition ${
                      ativo ? 'border-pagali-purple bg-pagali-purple/10' : 'border-gray-200 bg-white hover:border-pagali-purple-light'
                    } ${registado ? 'cursor-default' : ''}`}
                  >
                    <div className="flex items-start gap-3">
                      <span className={`mt-0.5 h-5 w-5 shrink-0 rounded border flex items-center justify-center text-xs ${
                        ativo ? 'bg-pagali-purple border-pagali-purple text-white' : 'border-gray-300'
                      }`}>{ativo ? '✓' : ''}</span>
                      <span>
                        <span className="block font-semibold text-gray-900">
                          {s.nome}
                          {sugerido && !registado && <span className="ml-2 text-xs font-medium text-pagali-purple">sugerido</span>}
                          {registado && <span className="ml-2 text-xs font-medium text-green-700">✓ registado</span>}
                        </span>
                        <span className="block text-sm text-gray-600 mt-0.5">{s.descricao}</span>
                      </span>
                    </div>
                  </button>
                );
              })}
            </div>
          </div>
        ))}

        <div className="h-24" />
      </div>
      <div className="fixed inset-x-0 bottom-0 bg-white border-t border-gray-200 shadow-lg">
        <div className="max-w-3xl mx-auto px-4 py-3 flex items-center gap-3">
          <p className="flex-1 text-sm text-gray-700">
            {total === 0
              ? 'Toque num serviço para registar o seu interesse.'
              : `${total} serviço${total > 1 ? 's' : ''} registado${total > 1 ? 's' : ''}. A nossa equipa vai contactá-lo.`}
          </p>
          <button
            type="button"
            onClick={() => setEnviado(true)}
            disabled={total === 0}
            className="bg-pagali-purple disabled:opacity-40 text-white rounded-xl px-6 py-3 font-semibold"
          >
            Concluir
          </button>
        </div>
      </div>
    </div>
  );
};

export default InteresseEntidade;
